import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  CART_ACTIONS,
  CART_SELECTORS,
  CART_SYNC_ACTIONS,
} from '@usf/ngrx-order';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { SearchAnalyticsService } from '../services/search-analytics.service';
import { CART_ANALYTICS_ACTIONS } from '@order/tracking/analytics/cart/cart-analytics.actions';
import { Tracking } from '@panamax/app-state';
import { genericAnalyticsTransformer } from '@shared/tracking/analytics/generic-analytics-transformer';

@Injectable({
  providedIn: 'root',
})
export class SearchAnalyticsEffects {
  /**
   * Effect that listens for cart updates and sends deferred search analytics
   * when an orderId becomes available after order creation
   */
  sendDeferredSearchAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CART_ACTIONS.loadCartIntoContext,
          CART_ACTIONS.updateCart,
          CART_SYNC_ACTIONS.cartSyncSuccess,
        ),
        filter(() => this.searchAnalyticsService.hasDeferredAnalytics()),
        switchMap(() =>
          this.store.select(CART_SELECTORS.selectCart).pipe(
            take(1),
            filter(cart => !!cart?.orderHeader?.orderId),
            map(cart => {
              const deferredAnalytics =
                this.searchAnalyticsService.getDeferredAnalytics();

              if (deferredAnalytics && cart?.orderHeader?.orderId) {
                // Create enhanced tracking data with the orderId using the same structure as normal flow
                const enhancedTracking: Tracking = {
                  analytics: {
                    data: {
                      ...deferredAnalytics.trackingData.analytics.data,
                      order: {
                        id: cart.orderHeader.orderId,
                      },
                      event: 'add to order',
                      capability: 'orders',
                    },
                    transformFunc: genericAnalyticsTransformer,
                  },
                };

                // Dispatch the same action that normal order flow uses
                this.store.dispatch(
                  CART_ANALYTICS_ACTIONS.trackAddToOrder({
                    tracking: enhancedTracking,
                  }),
                );
              }

              return null;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  /**
   * Cleanup effect to clear deferred analytics on cart reset or errors
   */
  clearDeferredSearchAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CART_ACTIONS.createNewCart,
          CART_ACTIONS.deleteCart,
          CART_ACTIONS.clearCart,
        ),
        map(() => {
          this.searchAnalyticsService.clearDeferredAnalytics();
          return null;
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private searchAnalyticsService: SearchAnalyticsService,
  ) {}
}
